import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage'
import { db, functionsAsia, storage } from '~~/firebaseConfig'

export const useContractStore = defineStore('contract', () => {
  const userStore = useUserStore()

  const contracts = ref<any[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)

  const currentView = ref<'list' | 'detail'>('list')

  // ⚠️ 2026-09-11: contract 컬렉션 write가 firestore.rules에서 전면 차단됨.
  // 생성·수정·서명·완료·삭제는 전부 functions/contract/contractActions.js onCall로만.
  // (contract.status가 콘텐츠 검수·정산 진행의 전제라, 클라이언트 직접 write는 위조 위험)
  // 목록/단건 조회(fetchUserContracts/getContract)는 그대로 client read.

  // 계약서 생성. 채팅 상대(targetUserId, targetUserType)와 계약을 만든다.
  // formData: { contractTitle, email, phone, startDate, endDate, contentField, productField, description, files }
  async function createContract(
    targetUserId: string,
    targetUserType: 'advertiser' | 'creator',
    formData: any,
    campaignTitle?: string,
    campaignId?: string,
  ): Promise<string | null> {
    try {
      loading.value = true
      error.value = null

      const { contractTitle, ...contractDetails } = formData || {}
      const fn = httpsCallable<any, { ok: boolean, contractId?: string, reason?: string }>(functionsAsia, 'createContract')
      const { data } = await fn({
        targetUserId,
        targetUserType,
        contractTitle,
        contractDetails,
        campaignTitle,
        campaignId,
      })
      if (!data.ok || !data.contractId) {
        error.value = data.reason || '계약서 생성에 실패했습니다.'
        return null
      }
      return data.contractId
    }
    catch (err) {
      error.value = err instanceof Error ? err.message : '계약서 생성에 실패했습니다.'
      return null
    }
    finally {
      loading.value = false
    }
  }

  // 계약서 상세 수정(draft 상태에서만).
  async function updateContract(contractId: string, formData: any): Promise<boolean> {
    try {
      loading.value = true
      error.value = null

      const { contractTitle: _t, ...contractDetails } = formData || {}
      const fn = httpsCallable<any, { ok: boolean, reason?: string }>(functionsAsia, 'updateContractDetails')
      const { data } = await fn({ contractId, contractDetails })
      if (!data.ok)
        error.value = data.reason || '계약서 수정에 실패했습니다.'
      return data.ok
    }
    catch (err) {
      error.value = err instanceof Error ? err.message : '계약서 수정에 실패했습니다.'
      return false
    }
    finally {
      loading.value = false
    }
  }

  // 계약 수락(서명). 양측이 각자 한 번씩 서명하면 status draft → accepted.
  async function signContract(contractId: string): Promise<{ ok: boolean, status?: string, reason?: string }> {
    try {
      loading.value = true
      error.value = null
      const fn = httpsCallable<{ contractId: string }, { ok: boolean, status?: string, reason?: string }>(functionsAsia, 'signContract')
      const { data } = await fn({ contractId })
      if (!data.ok)
        error.value = data.reason || '계약 수락에 실패했습니다.'
      return data
    }
    catch (err) {
      error.value = err instanceof Error ? err.message : '계약 수락에 실패했습니다.'
      return { ok: false, reason: error.value }
    }
    finally {
      loading.value = false
    }
  }

  // 계약 완료 처리(accepted → completed).
  async function markContractCompleted(contractId: string): Promise<{ ok: boolean, reason?: string }> {
    try {
      loading.value = true
      error.value = null
      const fn = httpsCallable<{ contractId: string }, { ok: boolean, reason?: string }>(functionsAsia, 'markContractCompleted')
      const { data } = await fn({ contractId })
      if (!data.ok)
        error.value = data.reason || '계약 완료 처리에 실패했습니다.'
      return data
    }
    catch (err) {
      error.value = err instanceof Error ? err.message : '계약 완료 처리에 실패했습니다.'
      return { ok: false, reason: error.value }
    }
    finally {
      loading.value = false
    }
  }

  // 사용자의 계약서 목록 조회
  async function fetchUserContracts() {
    try {
      loading.value = true
      error.value = null

      const currentUser = userStore.userStoreData
      if (!currentUser) {
        throw new Error('로그인이 필요합니다.')
      }

      const contractsCollection = collection(db, 'contract')
      let q

      if (currentUser.type === 'creator') {
        q = query(
          contractsCollection,
          where('creatorId', '==', currentUser.uid),
        )
      }
      else {
        q = query(
          contractsCollection,
          where('advertiserId', '==', currentUser.uid),
        )
      }

      const querySnapshot = await getDocs(q)
      const contractList = [] as any[]

      querySnapshot.forEach((doc) => {
        const data = doc.data()

        contractList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          signedAt: data.signedAt?.toDate(),
          completedAt: data.completedAt?.toDate(),
        } as any)
      })

      // 클라이언트 사이드에서 정렬
      contractList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

      contracts.value = contractList
      return contractList
    }
    catch (err) {
      console.error('계약서 목록 조회 실패:', err)
      error.value = err instanceof Error ? err.message : '계약서 목록 조회에 실패했습니다.'
      return []
    }
    finally {
      loading.value = false
    }
  }

  // 특정 계약서 조회
  async function getContract(contractId: string) {
    try {
      loading.value = true
      error.value = null

      const contractRef = doc(db, 'contract', contractId)
      const contractSnap = await getDoc(contractRef)

      if (contractSnap.exists()) {
        const data = contractSnap.data()
        return {
          id: contractSnap.id,
          ...data,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          signedAt: data.signedAt?.toDate(),
          completedAt: data.completedAt?.toDate(),
        }
      }
      else {
        error.value = '계약서를 찾을 수 없습니다.'
        return null
      }
    }
    catch (err) {
      console.error('계약서 조회 실패:', err)
      error.value = err instanceof Error ? err.message : '계약서 조회에 실패했습니다.'
      return null
    }
    finally {
      loading.value = false
    }
  }

  // 계약서 삭제(draft 상태에서만, onCall 경유).
  async function deleteContract(contractId: string): Promise<boolean> {
    try {
      loading.value = true
      error.value = null

      const fn = httpsCallable<{ contractId: string }, { ok: boolean, reason?: string }>(functionsAsia, 'deleteContract')
      const { data } = await fn({ contractId })
      if (!data.ok) {
        error.value = data.reason || '계약서 삭제에 실패했습니다.'
        return false
      }

      // 로컬 상태에서도 제거
      contracts.value = contracts.value.filter(contract => contract.id !== contractId)

      return true
    }
    catch (err) {
      console.error('계약서 삭제 실패:', err)
      error.value = err instanceof Error ? err.message : '계약서 삭제에 실패했습니다.'
      return false
    }
    finally {
      loading.value = false
    }
  }

  // 계약서 파일 업로드 함수
  async function uploadContractFile(file: File, contractId: string): Promise<{ name: string, url: string, type: string, uploadedAt: Date } | null> {
    try {
      const fileExtension = file.name.split('.').pop() || 'pdf'
      const cleanFileName = `contract_${Date.now()}.${fileExtension}`
      const fileName = `contract/${contractId}/${cleanFileName}`

      const fileRef = storageRef(storage, fileName)

      const metadata = {
        contentType: file.type,
        customMetadata: {
          originalName: file.name,
          contractId,
          uploadedAt: new Date().toISOString(),
        },
      }

      const snapshot = await uploadBytes(fileRef, file, metadata)
      const downloadURL = await getDownloadURL(snapshot.ref)

      return {
        name: file.name,
        url: downloadURL,
        type: file.type,
        uploadedAt: new Date(),
      }
    }
    catch (err) {
      console.error('파일 업로드 실패:', err)
      return null
    }
  }

  return {
    contracts,
    loading,
    error,
    currentView,
    createContract,
    updateContract,
    signContract,
    markContractCompleted,
    fetchUserContracts,
    getContract,
    deleteContract,
    uploadContractFile,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useContractStore, import.meta.hot))
